import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UserRole } from '../../shared/models/user-role.enum';
import { AccordionComponent } from '../components/dialog/student-faq/student-faq.component';
import { SidebarItem } from './sidebar-item.model';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [NgIf, NgClass, RouterLink, NgFor],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SidebarComponent implements OnInit {
  @Input() isOpen: boolean = true;
  @Output() toggle = new EventEmitter<void>();

  isMobile = false;
  currentRoute: string = '';
  userRole: UserRole | null = null;
  teacherMode: boolean = false;

  allMenuItems: SidebarItem[] = [];
  visibleMenuItems: SidebarItem[] = [];

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {
    const role = localStorage.getItem('userRole');
    this.teacherMode = role === 'teacher' ? true : false;
  }

  ngOnInit(): void {
    this.updateSidebarForScreenSize();
    this.currentRoute = this.router.url;

    const storedRole = localStorage.getItem('userRole');
    this.userRole = storedRole as UserRole;
    const dashboardRoute = this.getDashboardRoute(this.userRole);

    this.allMenuItems = [
      {
        label: UserRole.Student ? 'Home' : 'Dashboard',
        iconImagePath: UserRole.Student
          ? 'assets/icons/side-bar/student-home-icon.svg'
          : 'assets/icons/side-bar/home.svg',
        route: dashboardRoute,
        allowedRoles: [
          UserRole.CsqAdmin,
          UserRole.SuperAdmin,
          UserRole.CsqUser,
          UserRole.Teacher,
          UserRole.Student,
        ],
      },
      {
        label: 'User Management',
        iconImagePath: 'assets/icons/side-bar/user.svg',
        route: '/users',
        allowedRoles: [UserRole.CsqAdmin, UserRole.SuperAdmin],
      },
      {
        label: 'Event Management',
        iconImagePath: 'assets/icons/side-bar/event-manage.svg',
        route: '/events/event-list',
        allowedRoles: [
          UserRole.CsqAdmin,
          UserRole.SuperAdmin,
          UserRole.CsqUser,
        ],
        isParent: true,
        isExpanded: false,
        children: [
          {
            label: 'Event Type',
            iconImagePath: 'assets/icons/side-bar/task-square.svg',
            route: '/events/event-type',
            allowedRoles: [
              UserRole.CsqAdmin,
              UserRole.SuperAdmin,
              UserRole.CsqUser,
            ],
          },
          // {
          //     label: 'Event Attendance',
          //     iconImagePath: 'assets/icons/side-bar/task-square.svg',
          //     route: '/events/event-attendance',
          //     allowedRoles: [UserRole.CsqAdmin, UserRole.CsqUser],
          // },
          // {
          //     label: 'Event Managers',
          //     iconImagePath: 'assets/icons/side-bar/task-square.svg',
          //     route: '/events/event-managers',
          //     allowedRoles: [UserRole.CsqAdmin, UserRole.CsqUser],
          // },
          {
            label: 'Industry Partners',
            iconImagePath: 'assets/icons/side-bar/task-square.svg',
            route: '/events/event-mcc-partners',
            allowedRoles: [
              UserRole.CsqAdmin,
              UserRole.SuperAdmin,
              UserRole.CsqUser,
            ],
          },
          {
            label: 'Focus Area',
            iconImagePath: 'assets/icons/side-bar/task-square.svg',
            route: '/events/focus-area',
            allowedRoles: [
              UserRole.CsqAdmin,
              UserRole.SuperAdmin,
              UserRole.CsqUser,
            ],
          },
          {
            label: 'Audience',
            iconImagePath: 'assets/icons/side-bar/task-square.svg',
            route: '/events/audience',
            allowedRoles: [
              UserRole.CsqAdmin,
              UserRole.SuperAdmin,
              UserRole.CsqUser,
            ],
          },
          {
            label: 'Region',
            iconImagePath: 'assets/icons/side-bar/task-square.svg',
            route: '/events/region',
            allowedRoles: [
              UserRole.CsqAdmin,
              UserRole.SuperAdmin,
              UserRole.CsqUser,
            ],
          },
        ],
      },
      {
        label: 'Schools',
        iconImagePath: 'assets/icons/side-bar/school.svg',
        route: '/schools',
        allowedRoles: [
          UserRole.CsqAdmin,
          UserRole.SuperAdmin,
          UserRole.CsqUser,
        ],
      },
      {
        label: 'Teachers',
        iconImagePath: 'assets/icons/side-bar/teacher.svg',
        route: '/teachers',
        allowedRoles: [
          UserRole.CsqAdmin,
          UserRole.SuperAdmin,
          UserRole.CsqUser,
        ],
      },
      {
        label: 'Students',
        iconImagePath: 'assets/icons/side-bar/male-student.svg',
        route: '/students',
        allowedRoles: [
          UserRole.CsqAdmin,
          UserRole.SuperAdmin,
          UserRole.CsqUser,
          UserRole.Teacher,
        ],
      },
      {
        label: 'Reports',
        iconImagePath: 'assets/icons/side-bar/reports.svg',
        route: '/reports/report-dashboard',
        allowedRoles: [
          UserRole.CsqAdmin,
          UserRole.SuperAdmin,
          UserRole.CsqUser,
        ],
      },
      {
        label: 'E-Reg & attendance',
        iconImagePath: 'assets/icons/side-bar/male-student.svg',
        route: '/eventregistration-attendance',
        allowedRoles: [],
      },
      {
        label: 'CSQ Team',
        iconImagePath: 'assets/icons/side-bar/csq-users.svg',
        route: '/csq-team',
        allowedRoles: [UserRole.SuperAdmin],
      },
      {
        label: 'Settings',
        iconImagePath: 'assets/icons/side-bar/setting-2.svg',
        route: '/settings',
        allowedRoles: [
          UserRole.CsqAdmin,
          UserRole.SuperAdmin,
          UserRole.CsqUser,
        ],
        isParent: true,
        isExpanded: false,
        children: [
          {
            label: 'Work experience types',
            iconImagePath: 'assets/icons/side-bar/folder.svg',
            route: '/work-experience-type',
            allowedRoles: [UserRole.SuperAdmin],
          },
          // {
          //   label: 'Event category type',
          //   iconImagePath: 'assets/icons/side-bar/faq-icon.svg',
          //   route: '/event-category-type',
          //   allowedRoles: [UserRole.SuperAdmin],
          // },
          {
            label: 'Media Assets',
            iconImagePath: 'assets/icons/side-bar/image.svg',
            route: '/media-library',
            allowedRoles: [UserRole.SuperAdmin],
          },
          {
            label: 'Year level',
            iconImagePath: 'assets/icons/side-bar/graduation-cap.svg',
            route: '/year-level',
            allowedRoles: [UserRole.SuperAdmin],
          },
          {
            label: 'Ethnicity',
            iconImagePath: 'assets/icons/side-bar/globe.svg',
            route: '/ethnicity',
            allowedRoles: [UserRole.SuperAdmin],
          },
          {
            label: 'FAQ',
            iconImagePath: 'assets/icons/side-bar/faq-icon.svg',
            route: '/faq',
            allowedRoles: [UserRole.SuperAdmin],
          },
          {
            label: 'FAQ Categories',
            iconImagePath: 'assets/icons/side-bar/faq-icon.svg',
            route: '/faq/faq-categories',
            allowedRoles: [UserRole.SuperAdmin],
          },
        ],
      },

      //   {
      //     label: 'FAQ',
      //     iconImagePath: 'assets/icons/side-bar/faq-icon.svg',
      //     route: '/faq',
      //     allowedRoles: [
      //     //   UserRole.CsqAdmin,
      //     //   UserRole.SuperAdmin,
      //       UserRole.CsqUser,
      //       UserRole.Teacher,
      //     ],
      //     isParent: true,
      //     isExpanded: false,
      //     children: [
      //       {
      //         label: 'FAQ Categories',
      //         iconImagePath: 'assets/icons/side-bar/faq-icon.svg',
      //         route: '/faq/faq-categories',
      //         allowedRoles: [UserRole.SuperAdmin],
      //       },
      //     ],
      //   },
      // {
      //     label: 'My profile',
      //     iconImagePath: 'assets/icons/side-bar/student-profile-icon.svg',
      //     route: '/user/my-profile',
      //     allowedRoles: [UserRole.Student],
      // },
      // {
      //     label: 'point & achievements',
      //     iconImagePath: 'assets/icons/side-bar/event-manage.svg',
      //     route: '/user/point-and-achievements',
      //     allowedRoles: [UserRole.Student],
      // },
      // {
      //     label: 'Work experience',
      //     iconImagePath: 'assets/icons/side-bar/work-experience-icon.svg',
      //     // route: '/work-experience',
      //     allowedRoles: [UserRole.Student],
      // },
      {
        label: 'FAQ',
        iconImagePath: 'assets/icons/side-bar/info-icon.svg',
        onClick: () => this.openFaqDialog(),
        allowedRoles: [
          UserRole.Student,
          UserRole.Teacher,
          UserRole.CsqUser,
          UserRole.CsqAdmin,
        ],
      },
      {
        label: 'Settings',
        iconImagePath: 'assets/icons/side-bar/setting-2.svg',
        route: '/settings',
        allowedRoles: [UserRole.CsqUser, UserRole.Teacher, UserRole.Student],
      },
    ];

    this.visibleMenuItems = this.filterMenuItemsByRole(this.allMenuItems);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;
        this.updateExpandedState();
      });
    if (this.userRole === UserRole.Student) {
      this.renderer.setAttribute(
        this.elementRef.nativeElement.parentElement,
        'data-user-role',
        'student',
      );
    }
    this.updateExpandedState();
  }

  getDashboardRoute(role: UserRole | null): string {
    switch (role) {
      case UserRole.SuperAdmin:
        return '/dashboard/super-admin';
      case UserRole.CsqAdmin:
        return '/dashboard/csq-admin';
      case UserRole.CsqUser:
        return '/dashboard/csq-user';
      case UserRole.Teacher:
        return '/dashboard/teacher';
      case UserRole.Student:
        return '/dashboard/user';
      default:
        return '/unauthorized';
    }
  }

  private filterMenuItemsByRole(items: SidebarItem[]): SidebarItem[] {
    return items.filter((item) => {
      if (!item.allowedRoles || item.allowedRoles.includes(this.userRole!)) {
        if (item.children) {
          item.children = this.filterMenuItemsByRole(item.children);
          return item.children.length > 0;
        }
        return true;
      }
      return false;
    });
  }

  private updateExpandedState(): void {
    this.visibleMenuItems.forEach((item) => {
      item.isHovered = false;
      if (item.children) {
        item.children.forEach((child) => (child.isHovered = false));
      }
      if (
        this.userRole === UserRole.Student &&
        this.currentRoute.includes('/events/event-list/view/')
      ) {
        if (item.label === 'Event Management' && item.isExpanded) {
          item.isExpanded = false;
        }
        if (item.label === 'Home' && item.isExpanded) {
          item.isExpanded = false;
        }
      }

      if (
        this.userRole === UserRole.Teacher &&
        this.currentRoute.includes('/events/event-list/view/')
      ) {
        if (item.label === 'Event Management' && item.isExpanded) {
          item.isExpanded = false;
        }
        if (item.label === 'Home' && item.isExpanded) {
          item.isExpanded = false;
        }
      }
    });
  }

  isActive(route: string): boolean {
    if (!route) return false;
    const cleanCurrentRoute = this.currentRoute.split('?')[0];

    // Handle Student role
    if (this.userRole === UserRole.Student) {
      if (cleanCurrentRoute.includes('/events/event-list/view/')) {
        const dashboardRoute = this.getDashboardRoute(this.userRole);
        return route === dashboardRoute;
      }
    }

    // Handle Teacher role - THIS IS THE NEW CODE
    if (this.userRole === UserRole.Teacher) {
      if (cleanCurrentRoute.includes('/events/event-list/view/')) {
        const dashboardRoute = this.getDashboardRoute(this.userRole);
        return route === dashboardRoute;
      }
    }

    if (cleanCurrentRoute.includes('/events/event-list/view/')) {
      const dashboardRoute = this.getDashboardRoute(this.userRole);
      if (route === dashboardRoute) {
        return false;
      }
    }

    if (cleanCurrentRoute === route) {
      return true;
    }

    const routeWithSlash = route.endsWith('/') ? route : route + '/';
    if (cleanCurrentRoute.startsWith(routeWithSlash)) {
      const isChildRoute = this.visibleMenuItems.some((item) =>
        item.children?.some(
          (child) => child.route && cleanCurrentRoute === child.route,
        ),
      );
      if (isChildRoute) return false;
      return true;
    }

    return false;
  }

  isParentActive(item: SidebarItem): boolean {
    if (
      this.userRole === UserRole.Student &&
      this.currentRoute.includes('/events/event-list/view/')
    ) {
      if (item.label === 'Home' && this.isActive(item.route!)) {
        return true;
      }
      return false;
    }

    if (item.route && this.isActive(item.route)) {
      return true;
    }

    if (item.children) {
      return item.children.some(
        (child) => child.route && this.isActive(child.route),
      );
    }

    return false;
  }

  hasActiveChild(item: SidebarItem): boolean {
    if (!item.children) return false;
    return item.children.some(
      (child) => child.route && this.isActive(child.route),
    );
  }

  toggleSubmenu(item: SidebarItem): void {
    if (item.isParent && item.children) {
      if (!item.isExpanded) {
        if (item.route) {
          this.router.navigate([item.route]);
        }
        item.isExpanded = true;
      } else if (item.route && this.currentRoute === item.route) {
        item.isExpanded = !item.isExpanded;
      } else {
        if (item.route) {
          this.router.navigate([item.route]);
        }
        item.isExpanded = true;
      }
    }
  }

  isHoveringAnyItem: boolean = false;

  onMouseEnter(item: SidebarItem): void {
    this.isHoveringAnyItem = true;
    this.clearAllHoverStates();
    item.isHovered = true;
  }

  onMouseLeave(item: SidebarItem): void {
    item.isHovered = false;
    setTimeout(() => {
      const stillHovering = this.visibleMenuItems.some(
        (menuItem) =>
          menuItem.isHovered ||
          (menuItem.children &&
            menuItem.children.some((child) => child.isHovered)),
      );

      if (!stillHovering) {
        this.isHoveringAnyItem = false;
      }
    }, 100);
  }

  onSidebarMouseEnter(): void {
    if (!this.isOpen) {
      this.isHoveringAnyItem = true;
    }
  }

  onSidebarMouseLeave(): void {
    if (!this.isOpen) {
      this.isHoveringAnyItem = false;
      this.clearAllHoverStates();
    }
  }

  private clearAllHoverStates(): void {
    this.visibleMenuItems.forEach((item) => {
      item.isHovered = false;
      if (item.children) {
        item.children.forEach((child) => (child.isHovered = false));
      }
    });
  }

  closeHoverOnClick(item: SidebarItem): void {
    item.isHovered = false;
  }

  onSubmenuMouseEnter(parentItem: SidebarItem): void {
    parentItem.isHovered = true;
    this.isHoveringAnyItem = true;
  }

  onSubmenuMouseLeave(parentItem: SidebarItem): void {
    setTimeout(() => {
      parentItem.isHovered = false;
      const stillHovering = this.visibleMenuItems.some(
        (menuItem) =>
          menuItem.isHovered ||
          (menuItem.children &&
            menuItem.children.some((child) => child.isHovered)),
      );

      if (!stillHovering) {
        this.isHoveringAnyItem = false;
      }
    }, 100);
  }

  @HostListener('window:resize')
  onResize() {
    this.updateSidebarForScreenSize();
  }

  updateSidebarForScreenSize(): void {
    const isNowMobile = window.innerWidth <= 1024;
    if (isNowMobile !== this.isMobile) {
      this.isMobile = isNowMobile;
      this.isOpen = !isNowMobile;
    }
  }

  toggleSidebar(): void {
    this.toggle.emit();
  }

  logout(): void {
    localStorage.removeItem('userRole');
    localStorage.removeItem('authToken');
    this.router.navigate(['/auth/login']);
  }

  openFaqDialog(width: string = '50.5rem'): void {
    // Resolve audience: 0=Student, 1=Teacher, 2=CSQ
    const role = localStorage.getItem('userRole');
    let audienceType: 0 | 1 | 2 = 0;
    if (role === 'teacher') {
      audienceType = 1;
    } else if (role === 'csqadmin' || role === 'csquser') {
      audienceType = 2;
    }

    this.dialog.open(AccordionComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { audienceType },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });
  }
}
